import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faQuestion, faChevronRight, faCartShopping, faScrewdriverWrench, faGears } from "@fortawesome/free-solid-svg-icons";
import { SidebarToggle, SidebarMenuSelected } from "../pages/dashboard";
import sideBarLogo from '../assets/sideBarLogo.png';
import { useRoleContext } from "../utils/roleContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface Props {
    onToggle: SidebarToggle;
    onMenuSelected: SidebarMenuSelected;
    className?: string;
}

interface MenuItem {
    label: string;
    icon: any;
    subMenu?: string[];
}

interface MenuState {
    [key: string]: boolean;
}

const SideBar: React.FC<Props> = ({ onToggle, onMenuSelected, className = '' }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [openMenus, setOpenMenus] = useState<MenuState>({});
    const { userRole } = useRoleContext();

    const menuItems: MenuItem[] = [
        {
            label: "Reports", icon: faFileLines, subMenu: [
                'All Vehicles',
                'Report Missing',
                'Report Scanned',
                'Report-Extra Vehicles',
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Report Summary']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Lot History']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Scanned Vehicle Notes']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['User Devices'])
            ]
        },
        {
            label: "Tools", icon: faScrewdriverWrench, subMenu: [
                'Print Report',
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Manage Vehicle Status']),
                'Export Data',
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Vehicle Accessory']),
            ]
        },
        {
            label: "Shop", icon: faCartShopping, subMenu: [
                "Order Labels"
            ]
        },
        {
            label: "Settings", icon: faGears, subMenu: [
                ...(userRole === 'Admin' || userRole === 'SuperUser'  ? [] : ['DMS Connectivity']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Label Config']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Preferences']),
                'Download Label Print Support',
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['User List']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Enable QR-Code']),
                ...(userRole === 'Admin' || userRole === 'SuperUser' || userRole === 'Director' ? [] : ['Scheduled Reports']),
                'CSV Files For FileBased',
                'Column Selector',
                'Save grid settings',
                'Clear grid settings',
                ...(userRole === 'Admin' || userRole === 'SuperUser' ? ['Landing Page Upload'] : [])
            ]
        },
        {
            label: "Help", icon: faQuestion, subMenu: [
                'Request Remote Support',
                'User Instructions'
            ]
        },
    ]

    // Get responsive logo size based on sidebar state
    const getLogoSize = () => {
        if (collapsed) {
            return {
                width: '60px',
                height: 'auto',
                maxWidth: '100px'
            };
        } else {
            return {
                width: '180px',
                height: 'auto',
                maxWidth: '180px'
            };
        }
    };

    // Dispatch sidebar width change event whenever collapsed state changes
    useEffect(() => {
        const sidebarWidth = collapsed ? 60 : 250; // Adjust these values based on your actual sidebar widths
        window.dispatchEvent(new CustomEvent('sidebarResize', {
            detail: { width: sidebarWidth, collapsed: collapsed }
        }));
    }, [collapsed]);

    const toggle = () => {
        const newCollapsedState = !collapsed;
        setCollapsed(newCollapsedState);
        onToggle(newCollapsedState);
    }

    const menuSelected = (menu: string) => {
        if (menu === "Scheduled Reports") {
            window.location.href = '/dashboard/scheduleReport';
        } else if (menu === "Landing Page Upload") {
            window.location.href = '/dashboard/uploadLandingPage';
        } else {
            onMenuSelected(menu);
        }
    }

    return (
        <div className={`bg-blue-900 text-white h-full flex flex-col flex-grow transition-all duration-300 ease-in-out ${collapsed ? 'w-[5%] min-w-[60px]' : 'w-[10%] min-w-[180px]'} ${className}`}>
            <div className="flex-1 h-[100%]">
                {menuItems.map((item) => (
                    <div key={item.label}>
                        <div
                            className={`grid gap-3 items-center p-2 cursor-pointer ${!collapsed
                                    ? "grid-cols-[auto_1fr_auto]" // icon, text, chevron
                                    : item.subMenu
                                        ? "grid-cols-[auto_auto]" // icon, chevron only
                                        : "grid-cols-[auto]" // icon only
                                } ${openMenus[item.label] ? " active-menu" : ""}`}
                            onClick={() => {
                                if (!item.subMenu) {
                                    menuSelected(item.label);
                                    setOpenMenus({});
                                } else {
                                    const updated: MenuState = {};
                                    if (openMenus[item.label]) {
                                        // If the menu is already open, close it
                                        setOpenMenus({});
                                    } else {
                                        Object.keys(openMenus).forEach((key) => {
                                            updated[key] = false;
                                        });
                                        updated[item.label] = true;
                                        setOpenMenus(updated);
                                    }
                                }
                            }}
                        >
                            {/* FontAwesome Icon - always first column */}
                            <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />

                            {/* Text - second column, only when not collapsed */}
                            {!collapsed && (
                                <span className="text-left customFont">{item.label}</span>
                            )}

                            {/* Chevron - last column, only for submenus */}
                            {item.subMenu && (
                                <FontAwesomeIcon
                                    icon={faChevronRight}
                                    className={`w-4 h-4 transition duration-200 ${openMenus[item.label] ? "rotate-90" : ""
                                        }`}
                                />
                            )}
                        </div>

                        {/* Submenu items */}
                        {item.subMenu && openMenus[item.label] && (
                            <div className="sidebarOpen">
                                {item.subMenu.map(sub => (
                                    <div key={sub} className="cursor-pointer p-1" onClick={() => {
                                        menuSelected(sub)
                                    }}>
                                        <label className="customFont">{sub}</label>
                                        <hr />
                                    </div>
                                ))}
                            </div>
                        )}
                        <hr />
                    </div>
                ))}

                <div className="w-100 bottomImageStyles flex flex-col justify-center items-center">
                    <button
                        onClick={toggle}
                        className="w-full flex justify-start items-center p-2 hover:bg-blue-800 transition-colors rounded"
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? (
                            <ChevronRight className="w-5 h-5" />
                        ) : (
                            <ChevronLeft className="w-5 h-5" />
                        )}
                    </button>
                    <img
                        src={sideBarLogo}
                        alt="sidebar Branding"
                        className="transition-all duration-300 ease-in-out"
                        style={getLogoSize()}
                    />
                </div>
            </div>
        </div >
    )
}

export default SideBar